// Portal tickets list + new-ticket dialog (VII-207).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import portalApi from '../api';

const STAGE_COLOR = {
  todo: 'default',
  in_progress: 'info',
  done: 'success',
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'dd.MM.yyyy HH:mm'); } catch { return iso; }
}

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProviderId, setNewProviderId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const showProvider = providers.length > 1;

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, pRes] = await Promise.all([
        portalApi.get('/portal/tickets'),
        portalApi.get('/portal/providers'),
      ]);
      setTickets(tRes.data || []);
      setProviders(pRes.data || []);
      if (!newProviderId && pRes.data?.length) setNewProviderId(pRes.data[0].tenant_id);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleCreate = async () => {
    if (!newSubject.trim() || !newProviderId) return;
    setSubmitting(true);
    try {
      const res = await portalApi.post('/portal/tickets', {
        provider_tenant_id: newProviderId,
        subject: newSubject.trim(),
        body: newBody,
      });
      setDialogOpen(false);
      setNewSubject('');
      setNewBody('');
      navigate(`/portal/tickets/${res.data.provider_tenant_id}/${res.data.ticket_id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const sorted = useMemo(() => [...tickets], [tickets]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>My tickets</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)} disabled={!providers.length}>
          New ticket
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : sorted.length === 0 ? (
        <Alert severity="info">No tickets yet. Click <b>New ticket</b> to open your first one.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {showProvider && <TableCell>Provider</TableCell>}
                <TableCell>#</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last update</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((t) => (
                <TableRow
                  key={t.ticket_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/portal/tickets/${t.provider_tenant_id}/${t.ticket_id}`)}
                >
                  {showProvider && <TableCell>{t.provider_name}</TableCell>}
                  <TableCell>#{t.ticket_number}</TableCell>
                  <TableCell>{t.subject}</TableCell>
                  <TableCell>
                    <Chip size="small" label={t.stage_key} color={STAGE_COLOR[t.stage_key] || 'default'} />
                  </TableCell>
                  <TableCell>{fmtDate(t.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Subject" required value={newSubject} onChange={(e) => setNewSubject(e.target.value)} autoFocus />
            <TextField label="Describe your issue" multiline minRows={5} value={newBody} onChange={(e) => setNewBody(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitting || !newSubject.trim()}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
